import { Fragment, useMemo } from "react"

import { App, Col, Flex, Row, Tooltip, Typography } from "antd"
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import Private from "../../../../Components/Private"
import { useIncognito } from "../../../../hooks/useIncognito"
import Icon from "../../../../Icon"
import { modalConfig } from "../../../../theme"
import { extendedAccessRoles } from "../../../../Utils/permission"
import AppUtils, { ensureHttps } from "../../../../Utils/utils"
import { getColumns } from "../Table/columns"
import { getCreditRequestAttemptsMeta } from "./utils"

import "./styles.less"

const { Text, Link } = Typography

// `masked` marks a value as sensitive: it is replaced by the Incognito mask on screen (and loses its
// hover title so the raw value can't leak) while the copy button keeps working on the real value.
const CopyableValue = ({ value, copyValue = value, className, masked = false }) => {
  const incognito = useIncognito()
  const hidden = masked && incognito

  // AntD's built-in `copyable` icon (outlined) + its automatic "copied" check, matching the
  // CreditsPage overview (CHA copy-icon parity). `text` copies the real value even when masked.
  return (
    <div className={`overview-copy-row${className ? ` ${className}` : ""}`}>
      <Text strong className="overview-copy-row-text" title={hidden ? undefined : value}>
        {masked ? <Private>{value}</Private> : value}
      </Text>
      {/* Non-interactive wrapper only stops the copy click from bubbling to a clickable parent
          (kept from the old handler); the real control is AntD's copy button inside. */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <span onClick={(e) => e.stopPropagation()}>
        <Text copyable={{ tooltips: false, text: copyValue }} />
      </span>
    </div>
  )
}

// The sensitive plain-text rows (Alert ID, Billing descriptor, Transaction ID) all render the same
// way: a copyable row that shows the Incognito mask instead of the value — see CHA1-1352.
const maskedCopyableColumn = (isMobile) => ({
  render: (_) =>
    _ ? (
      <CopyableValue
        value={_}
        masked={true}
        className={isMobile ? "overview-copy-row-mobile" : ""}
      />
    ) : (
      <Text strong>—</Text>
    )
})

const dataList = {
  drawer: [
    {
      id: "alert_status",
      label: "Alert status"
    },
    {
      id: "business_account",
      label: "Business account"
    },
    {
      id: "merchant_descriptor",
      label: "Billing descriptor"
    },
    {
      id: "alert_service",
      label: "Alert source"
    },
    {
      id: "alert_id",
      label: "Alert ID"
    },
    {
      id: "alert_type",
      label: "Type"
    },
    {
      id: "alert_timestamp",
      label: "Alert date"
    },
    {
      id: "card_last4",
      label: "Card"
    },
    {
      id: "transaction_timestamp",
      label: "Transaction date",
      tooltip: "Date is provided from Alert source"
    },
    {
      id: "payment_processor",
      label: "Payment processor"
    },
    {
      id: "transaction_id",
      label: "Matched transaction ID",
      tooltip: "The transaction ID retrieved from the payment processor."
    },
    /* {
      id: 'transaction_timestamp',
      label: 'Matched transaction date',
      tooltip: 'The date and time of the transaction in the payment processor.',
    },*/
    {
      id: "amount",
      label: "Original order value"
    },
    {
      id: "converted_amount",
      label: "Converted order value"
    },
    {
      id: "paid_status",
      label: "Alert payment status"
    },
    {
      id: "arn",
      label: "ARN",
      tooltip: "Acquirer Reference Number is provided by alert source"
    },
    {
      id: "auth_code",
      label: "Auth.Code",
      tooltip: "Authorization Code is provided by alert source"
    },
    {
      id: "customer_full_name",
      label: "Customer full name"
    },
    {
      id: "customer_email",
      label: "Customer email"
    },
    {
      id: "credit_request_status",
      label: "Credit request status",
      validate: true
    },
    {
      id: "credit_request_rejection_reason",
      label: "Rejection reason",
      validate: true,
      validateRule: { credit_request_status: "Rejected" }
    },
    {
      id: "credit_request_attempts",
      label: "Credit request attempts"
    }
  ],
  modalForm: [
    {
      id: "alert_status",
      label: "Alert status"
    },
    {
      id: "alert_id",
      label: "Alert ID"
    },
    {
      id: "alert_timestamp",
      label: "Alert date"
    },
    {
      id: "alert_service",
      label: "Alert source"
    },
    {
      id: "amount",
      label: "Alert amount"
    },
    {
      id: "brand",
      label: "Cardholder"
    },
    {
      id: "transaction_id",
      label: "Transaction ID",
      tooltip: "The transaction ID retrieved from the payment processor."
    },
    {
      id: "transaction_timestamp",
      label: "Transaction date",
      tooltip: "Date is provided from Alert source"
    },
    {
      id: "business_account",
      label: "Business account"
    },
    {
      id: "merchant_descriptor",
      label: "Billing descriptor"
    },
    {
      id: "payment_processor",
      label: "Payment processor"
    },
    {
      id: "user_name",
      label: "User name"
    },
    {
      id: "user_email",
      label: "User email"
    }
  ],
  fullPageLeft: [
    {
      id: "alert_status",
      label: "Alert status"
    },
    {
      id: "business_account",
      label: "Business account"
    },
    {
      id: "business_url",
      label: "Business URL"
    },
    {
      id: "merchant_descriptor",
      label: "Billing descriptor"
    },
    {
      id: "alert_id",
      label: "Alert ID"
    },
    {
      id: "alert_service",
      label: "Alert source"
    },
    {
      id: "alert_type",
      label: "Type"
    },
    {
      id: "alert_reason",
      label: "Alert reason"
    },
    {
      id: "alert_timestamp",
      label: "Alert date"
    },
    {
      id: "card_last4",
      label: "Card"
    },
    {
      id: "transaction_timestamp",
      label: "Transaction date",
      tooltip: "Date is provided from Alert source"
    },
    {
      id: "enrollment_transaction_id",
      label: "Transaction ID"
      //tooltip: 'The transaction ID retrieved from the payment processor.',
    },
    {
      id: "country",
      label: "Country"
    },
    {
      id: "bank_name",
      label: "Bank"
    },
    {
      id: "amount",
      label: "Original order value"
    },
    {
      id: "converted_amount",
      label: "Converted order value"
    },
    {
      id: "arn",
      label: "ARN",
      tooltip: "Acquirer Reference Number is provided by alert source"
    },
    {
      id: "auth_code",
      label: "Auth.Code",
      tooltip: "Authorization Code is provided by alert source"
    }
  ],
  fullPageRight: [
    {
      id: "auto_refunds",
      label: "Auto refund"
    },
    {
      id: "payment_processor",
      label: "Payment processor"
    },
    {
      id: "transaction_id",
      label: "Matched transaction ID",
      tooltip: "The transaction ID retrieved from the payment processor."
    },
    /*{
      id: 'transaction_timestamp',
      label: 'Matched transaction date',
      tooltip: 'The date and time of the transaction in the payment processor.',
    },*/
    /*{
      id: 'matched_transaction_amount',
      label: 'Matched transaction amount',
    },*/
    {
      id: "shopify_fulfilment_status",
      label: "Shopify Status"
    },
    {
      id: "auto_refunded_at",
      label: "Auto-refund date"
    },
    {
      id: "customer_full_name",
      label: "Customer full name"
    },
    {
      id: "customer_email",
      label: "Customer email"
    },
    /*{
      id: 'customer_full_name',
      label: 'Customer full name',
    },*/
    {
      id: "unsubscribe_settings",
      label: "Auto-cancel subscription"
    },
    {
      id: "unsubscribe_status",
      label: "Subscription canceled"
    },
    {
      id: "unsubscribe_at",
      label: "Unsubscribe date"
    },
    {
      id: "paid_status",
      label: "Alert payment status"
    },
    {
      id: "credit_request_status",
      label: "Credit request status",
      validate: true
    },
    {
      id: "credit_request_rejection_reason",
      label: "Rejection reason",
      validate: true,
      validateRule: { credit_request_status: "Rejected" }
    },
    {
      id: "original_alert_id",
      label: "Original alert"
    },
    {
      id: "credit_request_attempts",
      label: "Credit request attempts"
    }
    /*{
      id: 'alert_invoice_id',
      label: 'Alert invoice ID',
    },*/
    /* {
      id: 'alert_invoice_timestamp',
      label: 'Alert invoice date',
    },*/
    /*{
      id: 'credit_request_status',
      label: 'Credit request status',
    },*/
  ]
}

const statusMap = {
  Pending: "warning",
  Paid: "success",
  "Not paid": "danger"
}

export const OverviewInfo = ({
  data = {},
  type,
  onOpenTransactionModal,
  onSubmitAlertStatusChange
}) => {
  const { t } = useTranslation()
  const { modal } = App.useApp()
  const isAdmin = useSelector((state) => state.auth.isAdmin)
  const isMobile = useSelector((state) => state.nav.isMobile)
  const ba_role = useSelector((state) => state.auth.userInfo?.ba_role?.id)
  const theme = useSelector((state) => state.nav.theme)
  const filterOptions = useSelector((state) => state.alerts.filterOptions)

  const businessAccountRole = isAdmin ? null : ba_role
  const list = dataList?.[type] || []

  const changeStatusConfirm = ({ alert, newStatus }) => {
    modal.confirm({
      ...modalConfig("info"),
      //centered: !isMobile,

      title: t("Alert status"),
      content: (
        <>
          <Text type="secondary">{t("Are you sure you want to change alert status?")}</Text>
          <br />
          <Text type="secondary">{t("Changing your alert status is an irreversible action.")}</Text>
        </>
      ),
      cancelText: t("Go back"),
      okText: t("Change status"),
      cancelButtonProps: { size: "large" },
      okButtonProps: { size: "large" },
      onOk() {
        onSubmitAlertStatusChange?.({ alert, newStatus })
      }
    })
  }

  const columnRender = useMemo(() => {
    const handleChangeStatus = ({ newStatus, record }) => {
      changeStatusConfirm({ alert: record, newStatus })
    }
    let options = filterOptions?.["alert_status"] || []

    return {
      ...getColumns({
        t,
        options,
        handleChangeStatus,
        theme,
        isAdmin,
        disabledStatus:
          type === "modalForm" ||
          (businessAccountRole && !extendedAccessRoles?.includes(businessAccountRole)),
        strong: true,
        withoutStatusTooltip: isMobile,
        onOpenTransactionModal
      }),
      amount: {
        render: (_, record) => (
          <Text strong>
            <Private>{`${record?.currency || ""} ${record?.amount}`}</Private>
          </Text>
        )
      },
      converted_amount: {
        render: (_, record) => (
          <Text strong>
            <Private>{`${record?.converted_currency || ""} ${record?.converted_amount}`}</Private>
          </Text>
        )
      },
      alert_timestamp: {
        render: (_) => <Text strong>{_ ? dayjs(_).format("DD MMM YYYY h:mm A [(UTC)]") : "—"}</Text>
      },
      transaction_timestamp: {
        render: (_) => <Text strong>{_ ? dayjs(_).format("DD MMM YYYY h:mm A [(UTC)]") : "—"}</Text>
      },
      auto_refunded_at: {
        render: (_) => <Text strong>{_ ? dayjs(_).format("DD MMM YYYY h:mm A [(UTC)]") : "—"}</Text>
      },
      unsubscribe_at: {
        render: (_) => <Text strong>{_ ? dayjs(_).format("DD MMM YYYY h:mm A [(UTC)]") : "—"}</Text>
      },
      paid_status: {
        render: (_) => (
          <Text type={statusMap?.[_]} strong>
            {_ || "—"}
          </Text>
        )
      },
      //credit_request_status: {render: (_) => <Text type={statusMap?.[_]} strong>{_ || '—'}</Text>},
      business_account: {
        render: (_) => (
          <Text strong>
            <Private>{_?.business_account}</Private>
          </Text>
        )
      },
      business_url: {
        render: (_, record) => {
          let link = _ || record?.business_account?.url
          if (!link) return <Text strong>—</Text>
          return (
            <div
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "185px"
              }}
            >
              {/* The whole link (not just its label) is replaced by the mask, so the href can't
                  leak the business URL through the browser status bar. */}
              <Private>
                <Link href={ensureHttps(link)} target="_blank" onClick={(e) => e.stopPropagation()}>
                  {link}
                </Link>
              </Private>
            </div>
          )
        }
      },
      alert_id: maskedCopyableColumn(isMobile),
      merchant_descriptor: maskedCopyableColumn(isMobile),
      enrollment_transaction_id: maskedCopyableColumn(isMobile),
      transaction_id: {
        render: (_, record) =>
          _?.length ? (
            <Flex gap={4} vertical={true}>
              {_?.map((id, idx) => (
                <Flex gap={4} align="center" key={id} style={{ minWidth: 0 }}>
                  <a
                    key={id}
                    href={record?.["transaction_link"]?.[idx] || record?.["transaction_url"]?.[idx]}
                    target="_blank"
                    onClick={(e) => e.stopPropagation()}
                    rel="noreferrer"
                    className="overview-link-ellipsis"
                  >
                    <Private>{id}</Private>
                  </a>
                  {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                  <span onClick={(e) => e.stopPropagation()}>
                    <Text copyable={{ tooltips: false, text: id }} />
                  </span>
                </Flex>
              ))}
            </Flex>
          ) : (
            "—"
          )
      },
      shopify_fulfilment_status: {
        render: (_) => <Text strong>{_ || "—"}</Text>
      },
      unsubscribe_status: {
        render: (_) => <Text strong>{_ ? "Yes" : "No"}</Text>
      },
      auto_refunds: {
        render: (_, record) => (
          <Text strong>
            {record?.["business_account"]?.["settings"]?.["is_use_auto_refund_other"] ||
            record?.["business_account"]?.["settings"]?.["is_use_auto_refund_rdr"]
              ? "Turned on"
              : "Turned off"}
          </Text>
        )
      },
      unsubscribe_settings: {
        render: (_, record) => (
          <Text strong>
            {record?.["business_account"]?.["unsubscribe_settings"] ? "Turned on" : "Turned off"}
          </Text>
        )
      },
      customer_email: {
        render: (_) => <Text strong>{_ ? <Private>{_}</Private> : "—"}</Text>
      },
      customer_full_name: {
        render: (value) => (
          <Text strong>
            {typeof value === "string" && value.trim() ? <Private>{value}</Private> : "—"}
          </Text>
        )
      },
      // `modalForm` (Contact support) builds this from the logged-in user's profile, so there is no
      // matching column in `getColumns` — it needs its own masked render.
      user_name: {
        render: (value) => (
          <Text strong>
            {typeof value === "string" && value.trim() ? <Private>{value}</Private> : "—"}
          </Text>
        )
      },
      credit_request_rejection_reason: {
        render: (_) => (_ ? <Text strong>{_}</Text> : null)
      },
      credit_request_status: {
        render: (_) => (_ ? <Text strong>{_}</Text> : null)
      },
      credit_request_attempts: {
        render: (_, record) => {
          const attemptsMeta = getCreditRequestAttemptsMeta(record)

          return (
            <Text strong className={attemptsMeta.className}>
              {attemptsMeta.value}
            </Text>
          )
        }
      }
    }
  }, [
    businessAccountRole,
    filterOptions,
    isAdmin,
    isMobile,
    onOpenTransactionModal,
    onSubmitAlertStatusChange,
    theme,
    type
  ])

  return (
    <Row gutter={[32, 6]}>
      {list
        .filter((el) =>
          data?.payment_processor !== "shopify" ? el?.id !== "shopify_fulfilment_status" : true
        )
        .filter(
          (el) =>
            el.id !== "converted_amount" ||
            (data?.converted_amount !== undefined && data?.converted_amount !== null)
        )
        .filter((el) =>
          el?.validateRule
            ? AppUtils.haveCommonFieldObjects(el?.validateRule, data)
            : el?.validate
              ? !!data?.[el?.id]
              : true
        )
        .filter((el) =>
          data?.alert_status !== "Duplicate" ? el?.id !== "original_alert_id" : true
        )
        ?.map((el) => (
          <Fragment key={el?.id}>
            <Col span={12} className="overview-info__col-mobile-430">
              <Flex gap={4} align="center">
                <Text type="secondary">{t(el?.label)}</Text>
                {el.id === "credit_request_attempts" && (
                  <Tooltip title={getCreditRequestAttemptsMeta(data).tooltip}>
                    <div className="credit-request-attempts__tooltip-trigger">
                      <Icon
                        type={getCreditRequestAttemptsMeta(data).tooltipIconType}
                        style={{
                          fontSize: 14,
                          color: getCreditRequestAttemptsMeta(data).tooltipIconColor
                        }}
                      />
                    </div>
                  </Tooltip>
                )}
                {el.tooltip && (
                  <Tooltip title={t(el.tooltip)}>
                    <div>
                      <Icon type="circle_info" style={{ fontSize: 14, color: "#7A7A83" }} />
                    </div>
                  </Tooltip>
                )}
              </Flex>
            </Col>
            <Col span={12} className="overview-info__col-mobile-430">
              {columnRender[el?.id]?.render ? (
                columnRender[el?.id]?.render(data?.[el?.id], data)
              ) : data?.[el?.id] ? (
                <CopyableValue
                  value={data?.[el?.id]}
                  className={isMobile ? "overview-copy-row-mobile" : ""}
                />
              ) : (
                <Text strong>—</Text>
              )}
            </Col>
          </Fragment>
        ))}
    </Row>
  )
}
